// The four heatmap.py palettes, plus its dB -> palette index mapping.
//
// Ported from spectrum-recorder's palette.ts, itself ported from
// reference/heatmap.py:322-361. Three of the four are reproduced exactly;
// `charolastra` is corrected, for the reason documented on it below.

export const PALETTE_NAMES = ['charolastra', 'default', 'extended', 'twente'] as const
export type PaletteName = (typeof PALETTE_NAMES)[number]

export const DEFAULT_PALETTE: PaletteName = 'charolastra'

export function isPaletteName(value: string): value is PaletteName {
  return (PALETTE_NAMES as readonly string[]).includes(value)
}

/** RGB triples, flattened: entry `i` is at `[3i, 3i+1, 3i+2]`. */
export type PackedPalette = Uint8Array

type Rgb = readonly [number, number, number]

/**
 * Python's `colorsys.hsv_to_rgb`, reproduced including its behavior outside
 * the documented input range: `int(h*6.0)` truncates toward zero before the
 * modulo, and Python's `%` is non-negative for a positive modulus.
 */
export function hsvToRgbColorsys(h: number, s: number, v: number): [number, number, number] {
  if (s === 0) return [v, v, v]
  const scaled = h * 6
  const truncated = Math.trunc(scaled)
  const f = scaled - truncated
  const p = v * (1 - s)
  const q = v * (1 - s * f)
  const t = v * (1 - s * (1 - f))
  switch (((truncated % 6) + 6) % 6) {
    case 0:
      return [v, t, p]
    case 1:
      return [q, v, p]
    case 2:
      return [p, v, t]
    case 3:
      return [p, q, v]
    case 4:
      return [t, p, v]
    default:
      return [v, p, q]
  }
}

/** heatmap.py's `int(c * 256)`: 256, not 255, so full scale needs clamping. */
function channelByte(value: number): number {
  const scaled = Math.trunc(value * 256)
  return scaled < 0 ? 0 : scaled > 255 ? 255 : scaled
}

function pack(entries: readonly Rgb[]): PackedPalette {
  const bytes = new Uint8Array(entries.length * 3)
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]!
    bytes[i * 3] = entry[0]
    bytes[i * 3 + 1] = entry[1]
    bytes[i * 3 + 2] = entry[2]
  }
  return bytes
}

/** `[(i, i, 50) for i in range(256)]`, the palette that made the reference PNG. */
function defaultPalette(): Rgb[] {
  const p: Rgb[] = []
  for (let i = 0; i < 256; i++) p.push([i, i, 50])
  return p
}

/** The same ramp, R/G dithered to three times the steps. */
function extendedPalette(): Rgb[] {
  const p: Rgb[] = [[0, 0, 50]]
  for (let i = 1; i < 256; i++) p.push([i, i - 1, 50], [i - 1, i, 50], [i, i, 50])
  return p
}

/** Blue -> magenta -> white; the flat white top is the original's stated intent. */
function twentePalette(): Rgb[] {
  const p: Rgb[] = []
  for (let i = 20; i < 100; i += 2) p.push([0, 0, i])
  for (let i = 0; i < 256; i++) {
    const g = i / 255
    p.push([Math.trunc(g * 255), 0, Math.trunc(g * 155) + 100])
  }
  for (let i = 0; i < 256; i++) p.push([255, i, 255])
  for (let i = 0; i < 100; i++) p.push([255, 255, 255])
  return p
}

/**
 * A hue sweep from dark blue to bright red, brightening as it goes.
 *
 * Corrected, and therefore the one palette that does not match heatmap.py.
 * The original `hsv_to_rgb(0.65 - (g - 0.08), 1, 0.2 + g)` runs its hue
 * negative past g = 0.73 and its value past 1.0 at g = 0.8, so PIL clips the
 * top quarter into flat red then flat magenta with a hard step between: 633
 * distinct colors out of 1024, and the broken region is exactly where
 * autoscale puts the strongest signals. Rescaling the evident intent to fit
 * the range keeps the same start color, ends on the red it was heading for,
 * and uses every entry.
 */
function charolastraPalette(): Rgb[] {
  const p: Rgb[] = []
  for (let i = 0; i < 1024; i++) {
    const g = i / 1023
    const [r, gr, b] = hsvToRgbColorsys(0.73 * (1 - g), 1, 0.2 + 0.8 * g)
    p.push([channelByte(r), channelByte(gr), channelByte(b)])
  }
  return p
}

const BUILDERS: Record<PaletteName, () => Rgb[]> = {
  charolastra: charolastraPalette,
  default: defaultPalette,
  extended: extendedPalette,
  twente: twentePalette,
}

const cache = new Map<PaletteName, PackedPalette>()

export function buildPalette(name: PaletteName): PackedPalette {
  let packed = cache.get(name)
  if (!packed) {
    packed = pack(BUILDERS[name]())
    cache.set(name, packed)
  }
  return packed
}

export function paletteLength(name: PaletteName): number {
  return buildPalette(name).length / 3
}

/**
 * heatmap.py's `rgb_inner`: linear normalization on dB, then a floor lookup.
 * Clamped at both ends, where Python would raise above the range and wrap the
 * weakest readings to the bright end below it.
 */
export function paletteIndex(z: number, minZ: number, maxZ: number, length: number): number {
  const span = maxZ - minZ
  if (!(span > 0)) return 0
  const scaled = Math.trunc(((z - minZ) / span) * (length - 1))
  return scaled < 0 ? 0 : scaled > length - 1 ? length - 1 : scaled
}
